import axios, { AxiosError } from "axios";
import type { ApiResponse, ApiErrorResponse } from "@learn-me/types";

const API_URL = import.meta.env.VITE_API_URL || "/api";

export const api = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Si hay varias peticiones 401 en paralelo, comparten un único refresh
// en vez de disparar uno por cada una.
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const storedRefreshToken = localStorage.getItem("refreshToken");
  if (!storedRefreshToken || storedRefreshToken === "undefined") {
    throw new Error("No refresh token");
  }

  const response = await axios.post<
    ApiResponse<{ accessToken: string; refreshToken?: string }>
  >(`${API_URL}/auth/refresh`, { refreshToken: storedRefreshToken });

  const data = response.data.data!;
  localStorage.setItem("accessToken", data.accessToken);
  // Solo sobrescribir el refresh token si el backend devolvió uno válido.
  if (data.refreshToken) {
    localStorage.setItem("refreshToken", data.refreshToken);
  }
  return data.accessToken;
}

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorResponse>) => {
    const originalRequest = error.config;

    // If 401 and not already retrying
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !(originalRequest as unknown as { _retry: boolean })._retry
    ) {
      (originalRequest as unknown as { _retry: boolean })._retry = true;

      try {
        // Reusar el refresh en curso si ya hay uno.
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const accessToken = await refreshPromise;

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch {
        // Refresh failed, clear tokens and redirect to login
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        // Evitar recarga en bucle si ya estamos en una pantalla de auth.
        const path = window.location.pathname;
        if (path !== "/login" && path !== "/register") {
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;
    return (
      axiosError.response?.data?.error?.message ||
      "An unexpected error occurred"
    );
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unexpected error occurred";
}
